import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "batisur_pwa_install_dismissed";

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = window.navigator.userAgent;
    const ios = /iPhone|iPad|iPod/i.test(ua);
    const standalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setIsIOS(ios);
    setInstalled(standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt && Date.now() - Number(dismissedAt) < 7 * 24 * 3600 * 1000) return;
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Show iOS hint if iOS + not installed + not dismissed
    if (ios && !standalone) {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (!dismissedAt || Date.now() - Number(dismissedAt) > 7 * 24 * 3600 * 1000) {
        setTimeout(() => setVisible(true), 3000);
      }
    }

    window.addEventListener("appinstalled", () => {
      setVisible(false);
      setInstalled(true);
    });

    // Manual trigger from Navbar
    const showManual = () => {
      if (!standalone) setVisible(true);
    };
    window.addEventListener("batisur:show-pwa", showManual);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("batisur:show-pwa", showManual);
    };
  }, []);

  if (installed || !visible) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome !== "accepted") {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-40 bg-[#0A0A0B] text-white border-2 border-[#FF4D00] shadow-[6px_6px_0_#FF4D00]"
      data-testid="pwa-install-banner"
    >
      <div className="p-4 flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 bg-[#FF4D00] flex items-center justify-center">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00]">// Installer Batisur</div>
          <div className="display text-lg mt-1 leading-tight">
            {isIOS ? "Ajoutez Batisur à votre écran d'accueil" : "Installez Batisur sur votre téléphone"}
          </div>
          <div className="text-xs text-zinc-400 mt-1">
            {isIOS
              ? "Appuyez sur « Partager » puis « Sur l'écran d'accueil » pour installer."
              : "Accès instantané en un clic, comme une vraie app · Zéro store · Zéro Mo."}
          </div>
          <div className="mt-3 flex gap-2">
            {!isIOS && deferredPrompt && (
              <button
                onClick={install}
                className="btn-batisur flex items-center gap-1.5 text-xs"
                data-testid="pwa-install-btn"
              >
                <Download className="w-3.5 h-3.5" /> Installer
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-xs mono uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2"
              data-testid="pwa-dismiss-btn"
            >
              Plus tard
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-zinc-500 hover:text-white shrink-0"
          aria-label="Fermer"
          data-testid="pwa-close-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
